import { useState } from "react";

import { useLocation } from "react-router-dom";

import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";

import MKBox from "components/MKBox";
import MKButton from "components/MKButton";
import MKInput from "components/MKInput";
import MKTypography from "components/MKTypography";
import DefaultFooter from "examples/Footers/DefaultFooter";
import DefaultNavbar from "examples/Navbars/DefaultNavbar";
import DefaultInfoCard from "examples/Cards/InfoCards/DefaultInfoCard";

import footerRoutes from "footer.routes";
import routes from "routes";
import FaqAccordionList from "studio-edifique/components/FaqAccordionList";
import SectionHeading from "studio-edifique/components/SectionHeading";
import SeoHead from "studio-edifique/components/SeoHead";
import { trackContactIntent } from "studio-edifique/components/Analytics";
import {
  company,
  contactFaqs,
  createBreadcrumbSchema,
  createFaqSchema,
  createOrganizationSchema,
  heroImages,
  services,
} from "studio-edifique/siteContent";

const initialState = {
  fullName: "",
  email: "",
  service: services[0].title,
  message: "",
};

function ContactPage() {
  const { pathname } = useLocation();
  const [formData, setFormData] = useState(initialState);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((currentState) => ({
      ...currentState,
      [name]: value,
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    const message = [
      "Ola, Studio Edifique.",
      "",
      `Nome: ${formData.fullName}`,
      `E-mail: ${formData.email}`,
      `Servico de interesse: ${formData.service}`,
      "",
      "Mensagem:",
      formData.message,
    ]
      .filter(Boolean)
      .join("\n");

    trackContactIntent("whatsapp");
    window.open(
      `${company.whatsappUrl}?text=${encodeURIComponent(message)}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  return (
    <>
      <SeoHead
        title="Contato"
        description="Entre em contato com a Studio Edifique para projetos, interiores, laudos, fiscalização, administração de obra e construção."
        keywords={[
          ...company.seoKeywords,
          "contato Studio Edifique",
          "orçamento arquitetura Itatiba",
        ]}
        path={pathname}
        image={heroImages.contact}
        schema={[
          createOrganizationSchema(),
          createBreadcrumbSchema([
            { name: "Início", path: "/" },
            { name: "Contato", path: pathname },
          ]),
          createFaqSchema(contactFaqs),
        ]}
      />
      <MKBox position="fixed" top="0.5rem" width="100%" zIndex={10}>
        <DefaultNavbar
          brand={company.brandName}
          routes={routes}
          action={{
            type: "external",
            route: company.whatsappUrl,
            label: "WhatsApp",
            color: "info",
          }}
        />
      </MKBox>
      <Grid container spacing={3} alignItems="center">
        <Grid item xs={12} lg={6}>
          <MKBox
            display={{ xs: "none", lg: "flex" }}
            width="calc(100% - 2rem)"
            height="calc(100vh - 2rem)"
            borderRadius="lg"
            ml={2}
            mt={2}
            sx={{
              backgroundImage: ({ functions: { linearGradient, rgba } }) =>
                `${linearGradient(rgba("#1F1B1A", 0.3), rgba("#1F1B1A", 0.28))}, url(${
                  heroImages.contact
                })`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          />
        </Grid>
        <Grid
          item
          xs={12}
          sm={10}
          md={8}
          lg={6}
          xl={4}
          ml={{ xs: "auto", lg: 6 }}
          mr={{ xs: "auto", lg: 6 }}
        >
          <MKBox
            bgColor="white"
            borderRadius="xl"
            shadow="lg"
            display="flex"
            flexDirection="column"
            justifyContent="center"
            mt={{ xs: 20, sm: 18, md: 20 }}
            mb={{ xs: 12, sm: 14, md: 16 }}
            mx={3}
          >
            <MKBox
              variant="gradient"
              bgColor="info"
              coloredShadow="info"
              borderRadius="lg"
              p={2}
              mx={2}
              mt={-3}
            >
              <MKTypography variant="h3" component="h1" color="white">
                Fale com a Studio Edifique
              </MKTypography>
            </MKBox>
            <MKBox p={3}>
              <MKTypography variant="body2" color="text" mb={3}>
                Preencha os dados abaixo e o envio abrirá uma conversa no WhatsApp com a sua
                mensagem já estruturada. Conte qual serviço você procura e em que etapa está o seu
                projeto ou sua obra.
              </MKTypography>
              <MKBox display="flex" flexWrap="wrap" gap={1.5} mb={3}>
                <MKButton
                  component="a"
                  href={company.emailUrl}
                  variant="outlined"
                  color="dark"
                  size="small"
                >
                  {company.email}
                </MKButton>
                <MKButton
                  component="a"
                  href={company.siteUrl}
                  target="_blank"
                  rel="noreferrer"
                  variant="outlined"
                  color="dark"
                  size="small"
                >
                  {company.siteDisplayUrl}
                </MKButton>
              </MKBox>
              <MKBox component="form" onSubmit={handleSubmit} autoComplete="off">
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <MKInput
                      variant="standard"
                      label="Nome completo"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleChange}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <MKInput
                      type="email"
                      variant="standard"
                      label="E-mail"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      required
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      select
                      variant="standard"
                      label="Serviço de interesse"
                      name="service"
                      value={formData.service}
                      onChange={handleChange}
                      fullWidth
                    >
                      {services.map((service) => (
                        <MenuItem key={service.title} value={service.title}>
                          {service.title}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12}>
                    <MKInput
                      variant="standard"
                      label="Como podemos ajudar?"
                      placeholder="Conte um pouco sobre o projeto, a obra ou a necessidade tecnica."
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      InputLabelProps={{ shrink: true }}
                      multiline
                      fullWidth
                      rows={6}
                      required
                    />
                  </Grid>
                </Grid>
                <Grid container item justifyContent="center" xs={12} mt={5} mb={1}>
                  <MKButton type="submit" variant="gradient" color="info">
                    Enviar via WhatsApp
                  </MKButton>
                </Grid>
              </MKBox>
            </MKBox>
          </MKBox>
        </Grid>
      </Grid>

      <MKBox component="section" py={10}>
        <Container>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6} lg={3}>
              <DefaultInfoCard
                icon="phone_iphone"
                title="WhatsApp"
                description={`${company.phoneDisplay} para atendimento inicial, alinhamento de escopo e envio de informações.`}
              />
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <DefaultInfoCard
                icon="mail"
                title="E-mail institucional"
                description={`${company.email} para contatos formais, solicitações e continuidade do atendimento.`}
              />
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <DefaultInfoCard
                icon="location_city"
                title="Cidade de atuação"
                description="Atendimento com base em Itatiba, com análise de demandas compatíveis em toda a região."
              />
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <DefaultInfoCard
                icon="photo_camera"
                title="Instagram"
                description={`Acompanhe bastidores, projetos e atualizações pelo perfil ${company.instagramHandle}.`}
              />
            </Grid>
          </Grid>
        </Container>
      </MKBox>

      <MKBox
        component="section"
        py={8}
        sx={{
          background: ({ palette: { grey } }) => grey[100],
        }}
      >
        <Container>
          <Grid container spacing={6}>
            <Grid item xs={12} lg={5}>
              <SectionHeading
                eyebrow="Contato e atendimento"
                title="Informações objetivas para acelerar o primeiro passo"
                description="Se a sua dúvida não estiver aqui, envie uma mensagem pelo WhatsApp e organize o atendimento de forma direta."
              />
            </Grid>
            <Grid item xs={12} lg={7}>
              <FaqAccordionList items={contactFaqs} />
            </Grid>
          </Grid>
        </Container>
      </MKBox>

      <MKBox pt={6} px={1} mt={6}>
        <DefaultFooter content={footerRoutes} />
      </MKBox>
    </>
  );
}

export default ContactPage;
